import { Link } from '@inertiajs/react'
import type { RocksStats, RockSummary } from '../../types'

function pctColor(pct: number): string {
  if (pct >= 100) return '#1B7340'
  if (pct >= 75) return '#5BA84B'
  if (pct >= 50) return '#FBA737'
  if (pct >= 25) return '#F08A3A'
  return '#ED1C24'
}

function fmtPct(p: number): string {
  return p % 1 === 0 ? `${p}%` : p.toFixed(1) + '%'
}

export default function RocksSummaryCard({ stats, teamId }: { stats: RocksStats; teamId: number }) {
  return (
    <div className="bg-white rounded-lg border border-stone">
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone">
        <h3 className="text-base font-bold uppercase tracking-widest text-text-secondary">
          F.A.S.T. Rocks — {stats.quarter_label}
        </h3>
        <Link href={`/teams/${teamId}/rocks`} className="text-sm font-semibold text-text-primary hover:underline">
          View All →
        </Link>
      </div>

      {stats.total > 0 && (
        <div className="px-5 pt-4">
          <div className="flex items-baseline gap-3">
            <span className="text-2xl font-bold text-text-primary">{fmtPct(stats.average_percent)}</span>
            <span className="text-sm text-text-muted">average completion</span>
          </div>
          <div className="mt-2 h-2.5 rounded-full overflow-hidden bg-stone/30">
            <div
              className="h-2.5 rounded-full"
              style={{ width: `${stats.average_percent}%`, backgroundColor: pctColor(stats.average_percent) }}
            />
          </div>
          <div className="mt-2 text-sm text-text-secondary">
            <span className="font-semibold">{stats.count_at_100}</span> of {stats.total} at 100%
          </div>
        </div>
      )}

      <div className="p-5">
        {stats.rocks_list.length > 0 ? (
          <div className="space-y-2.5">
            {stats.rocks_list.slice(0, 7).map((r: RockSummary) => {
              const c = pctColor(r.percent_complete)
              return (
                <div key={r.id} className="flex items-center gap-3">
                  <span
                    className="text-[12px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap tabular-nums"
                    style={{ backgroundColor: `${c}26`, color: c }}
                  >
                    {fmtPct(r.percent_complete)}
                  </span>
                  <span className="text-base text-text-primary flex-1 truncate">{r.title}</span>
                  {r.owner && (
                    <div
                      className="w-6 h-6 rounded-full bg-charcoal/20 text-text-primary text-[12px] font-bold flex items-center justify-center flex-shrink-0"
                      title={`${r.owner.first_name} ${r.owner.last_name}`}
                    >
                      {r.owner.initials}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-base text-text-secondary text-center py-4">
            No rocks set for this quarter.{' '}
            <Link href={`/teams/${teamId}/rocks`} className="text-text-primary hover:underline">
              Add rocks
            </Link>
          </p>
        )}
      </div>
    </div>
  )
}
